import { createApi } from '@reduxjs/toolkit/query/react';
import { endPoints, urlKeys } from 'src/api/APIEndpoints';
import { formatString } from 'src/util/STR';
import { baseQuery } from './baseQuery';

export const ipManagementApi = createApi({
  baseQuery,
  reducerPath: 'ipManagementApi',
  endpoints: builder => ({
    getIpReleaseData: builder.query<any, any>({
      query: ({ site_id, study_id, country, application }) => ({
        url: `${formatString(
          endPoints.IpRelease.getAll,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}`,
        method: 'GET'
      }),
      extraOptions: { maxRetries: 3 }
    }),
    createIpRelease: builder.query<any, any>({
      query: ({ site_id, study_id, country, application, data }) => ({
        url: `${formatString(
          endPoints.IpRelease.create,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}`,
        method: 'POST',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    updateIpRelease: builder.query<any, any>({
      query: ({ site_id, study_id, country, application, data }) => ({
        url: `${formatString(
          endPoints.IpRelease.update,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}`,
        method: 'PUT',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    getIpSupplyDetailsData: builder.query<any, any>({
      query: ({ site_id, study_id, country, application }) => ({
        url: `${formatString(
          endPoints.IPSupplyDetails.select,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}`,
        method: 'GET'
      }),
      extraOptions: { maxRetries: 3 }
    }),
    getIpSupplyOrderData: builder.query<any, any>({
      query: ({ site_id, study_id, country, application }) => ({
        url: `${formatString(
          endPoints.IPSupplyOrder.select,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}`,
        method: 'GET'
      }),
      extraOptions: { maxRetries: 3 }
    }),
    updateIpSupplyOrder: builder.mutation<any, any>({
      query: ({ site_id, study_id, country, application, data, application_section }) => ({
        url: `${formatString(
          endPoints.IPSupplyOrder.update,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}&application_section=${application_section}`,
        method: 'PUT',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    createIpSupplyOrder: builder.query<any, any>({
      query: ({ site_id, study_id, country, application, data, application_section }) => ({
        url: `${formatString(
          endPoints.IPSupplyOrder.create,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}&application_section=${application_section}`,
        method: 'POST',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    getDRugNames: builder.query<any, any>({
      query: ({ site_id, study_id, country, application }) => ({
        url: `${formatString(
          endPoints.IPSupplyOrder.getDrugName,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}`,
        method: 'GET'
      }),
      extraOptions: { maxRetries: 3 }
    }),
    ipSupplySendMail: builder.query<any, any>({
      query: ({ site_id, study_id, country, application, application_section, data }) => ({
        url: `${formatString(
          endPoints.IPSupplyOrder.sendEmail,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}&application_section=${application_section}`,
        method: 'POST',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    downloadCSV: builder.query<any, any>({
      query: ({ site_id, study_id, country, ip_supply_id, application }) => ({
        url: `${formatString(
          endPoints.IPSupplyOrder.downloadCSV,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.ip_supply_id, value: ip_supply_id }
        )}?country=${country}&application=${application}`,
        method: 'POST',
        responseHandler: async response => window.location.assign(window.URL.createObjectURL(await response.blob()))
      }),
      extraOptions: { maxRetries: 3 }
    }),
    approveSubmit: builder.query<any, any>({
      query: ({ site_id, study_id, country, ip_supply_id, application, data, application_section }) => ({
        url: `${formatString(
          endPoints.IPSupplyOrder.approveUpdate,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.ip_supply_id, value: ip_supply_id }
        )}?country=${country}&application=${application}&application_section=${application_section}`,
        method: 'PUT',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    getAllReSupply: builder.mutation<any, any>({
      query: ({ study_id, site_id, country, application, application_section, data, from, size }) => ({
        url: `${formatString(
          endPoints.ipResupplyOrder.getAllResupply,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}&application_section=${application_section}${
          from > -1 ? `&from=${from}` : ''
        }${size > -1 ? `&size=${size}` : ''}`,
        method: 'POST',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    selectIpRerupplyOrder: builder.mutation<any, any>({
      query: ({ site_id, study_id, country, ip_supply_id, application, application_section }) => ({
        url: `${formatString(
          endPoints.ipResupplyOrder.selectedResupply,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: urlKeys.ip_supply_id, value: ip_supply_id }
        )}?country=${country}&application=${application}&application_section=${application_section}`,
        method: 'GET'
      }),
      extraOptions: { maxRetries: 3 }
    }),
    updateIpResupplyOrder: builder.mutation<any, any>({
      query: ({ site_id, study_id, country, ip_resupply_id, application, data, application_section }) => ({
        url: `${formatString(
          endPoints.ipResupplyOrder.updateResupply,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id },
          { key: `ip_resupply_id`, value: ip_resupply_id }
        )}?country=${country}&application=${application}&application_section=${application_section}`,
        method: 'PUT',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    }),
    getAllResupplyConfirm: builder.mutation<any, any>({
      query: ({ study_id, site_id, country, application, application_section, data, from, size }) => ({
        url: `${formatString(
          endPoints.ipResupplyOrder.getAllConfirm,
          { key: urlKeys.site_id, value: site_id },
          { key: urlKeys.study_id, value: study_id }
        )}?country=${country}&application=${application}&application_section=${application_section}${
          from > -1 ? `&from=${from}` : ''
        }${size > -1 ? `&size=${size}` : ''}`,
        method: 'POST',
        body: data
      }),
      extraOptions: { maxRetries: 3 }
    })
  })
});

export const {
  useGetIpReleaseDataQuery,
  useLazyCreateIpReleaseQuery,
  useLazyUpdateIpReleaseQuery,
  useGetIpSupplyDetailsDataQuery,
  useLazyGetIpReleaseDataQuery,
  useGetIpSupplyOrderDataQuery,
  useUpdateIpSupplyOrderMutation,
  useLazyCreateIpSupplyOrderQuery,
  useGetDRugNamesQuery,
  useIpSupplySendMailQuery,
  useLazyIpSupplySendMailQuery,
  useLazyDownloadCSVQuery,
  useLazyApproveSubmitQuery,
  useLazyGetIpSupplyOrderDataQuery,
  useGetAllReSupplyMutation,
  useSelectIpRerupplyOrderMutation,
  useUpdateIpResupplyOrderMutation,
  useGetAllResupplyConfirmMutation
} = ipManagementApi;
